import * as React from "react";
import { ELanguageLevel } from "types/api/language";
import { CircleProgressProps } from "./CircleProgress.types";
import "./CircleProgress.styles.scss";

// Utility function to convert backend language level to display format
const formatLanguageLevel = (backendLevel: string): string => {
  const levelEntry = Object.entries(ELanguageLevel).find(
    ([, value]) => value === backendLevel
  );
  return levelEntry ? levelEntry[0] : backendLevel;
};

export const CircleProgress: React.FC<CircleProgressProps> = ({
  progress,
  centerText,
  size = 170,
  strokeWidth = 15,
  centerTextSize = "32px",
  percentageTextSize = "16px",
  className = "",
}) => {
  const center = size / 2;
  // Calculate radius to keep the outer edge of stroke at the container boundary
  // SVG stroke extends strokeWidth/2 in both directions from the path
  const radius = Math.max(10, center - strokeWidth / 2);
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Format the language level for display
  const formattedCenterText = formatLanguageLevel(centerText);

  // Determine color based on progress value
  // 0-25: Red (error), 25-50: Light Orange, 50-75: Yellow, 75-100: Green
  const getProgressColor = (value: number): string => {
    if (value >= 0 && value < 25) return "error"; // Red
    if (value >= 25 && value < 50) return "light-orange"; // Light Orange
    if (value >= 50 && value < 75) return "yellow"; // Yellow
    if (value >= 75 && value <= 100) return "green"; // Green
    return "light-orange"; // Default fallback
  };

  const progressColor = getProgressColor(progress);

  return (
    <div className={`nb-circle-progress ${className}`}>
      <svg
        className="nb-circle-progress-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Background circle */}
        <circle
          className="nb-circle-progress-background"
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          className={`nb-circle-progress-foreground nb-circle-progress-foreground--${progressColor}`}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="nb-circle-progress-text">
        <span
          className={`nb-circle-progress-center-text nb-circle-progress-center-text--${progressColor}`}
          style={{ fontSize: centerTextSize }}
        >
          {formattedCenterText}
        </span>
        <span
          className="nb-circle-progress-percentage"
          style={{ fontSize: percentageTextSize }}
        >
          {progress}%
        </span>
      </div>
    </div>
  );
};
